import styled, { keyframes, css } from 'styled-components';
import { getMargins, getTypographyStyles, CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_LG_LABEL, CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_LG_VALUE, CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_MD_RANGE, CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_MD_VALUE, CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_MD_LABEL, CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_SM_LABEL, CMP_BORDER_WIDTH_FEEDBACK_PROGRESS_INDICATOR_SPINNER_LG, CMP_BORDER_WIDTH_FEEDBACK_PROGRESS_INDICATOR_SPINNER_MD, SYS_SPACE_FIXED_100, SYS_SIZE_FIXED_100, SYS_BORDER_RADIUS_MAX } from '@oneaudi/unified-web-alpha-common';
const continuousSpinningKeyframes = keyframes `
  from {
    transform: rotate(-90deg);
  }
  to {
    transform: rotate(270deg);
  }
`;
const dashLengthFromAngle = (circumference, deg) => `${(deg / 360) * circumference}px`;
const continuousCircularLoadingKeyframes = (circumference) => keyframes `
  0% {
    stroke-dasharray: ${[0, 360]
    .map((d) => dashLengthFromAngle(circumference, d))
    .join(',')};
    stroke-dashoffset: ${dashLengthFromAngle(circumference, 0)};
    // TODO: token needed
    animation-timing-function: ease-in;
  }
  20% {
    stroke-dasharray: ${[200, 360]
    .map((d) => dashLengthFromAngle(circumference, d))
    .join(',')};
    stroke-dashoffset: ${dashLengthFromAngle(circumference, -40)};
  }
  100% {
    stroke-dasharray: ${[200, 360]
    .map((d) => dashLengthFromAngle(circumference, d))
    .join(',')};
    stroke-dashoffset: ${dashLengthFromAngle(circumference, -360)};
  }
`;
const calculateCircumference = (size, strokeWidth) => Math.PI * (size - strokeWidth);
export const StyledInfiniteSpinner = styled.svg `
  margin: ${(props) => getMargins(props.margin)};
  display: block;
  width: ${({ dimensions }) => dimensions}px;
  height: ${({ dimensions }) => dimensions}px;
  transform-origin: 50% 50%;
  animation: ${continuousSpinningKeyframes} 1.5s cubic-bezier(0.4, 0, 0.3, 1)
    infinite;
`;
export const StyledFiniteSpinner = styled.svg `
  margin: ${(props) => getMargins(props.margin)};
  display: block;
  width: ${({ dimensions }) => dimensions}px;
  height: ${({ dimensions }) => dimensions}px;
  transform-origin: 50% 50%;

  text {
    fill: ${(props) => props.theme.cmp.color.feedback.progressIndicator.foreground.text.default};
    ${(props) => props.size === 'md'
    ? getTypographyStyles(CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_MD_VALUE)
    : getTypographyStyles(CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_LG_VALUE)}
  }
`;
export const StyledTrack = styled.circle `
  fill: none;
  border: none;
  ${(props) => props.colorScheme === 'inverted'
    ? css `
          stroke: ${props.theme.cmp.color.feedback.progressIndicator.inverted
        .surface.trackLine.default};
        `
    : css `
          stroke: ${props.theme.cmp.color.feedback.progressIndicator.monochrome
        .surface.trackLine.default};
        `}
  stroke-width: ${(props) => props.size === 'lg' || (typeof props.size === 'number' && props.size >= 48)
    ? CMP_BORDER_WIDTH_FEEDBACK_PROGRESS_INDICATOR_SPINNER_LG
    : CMP_BORDER_WIDTH_FEEDBACK_PROGRESS_INDICATOR_SPINNER_MD};
`;
export const StyledTrain = styled.circle `
  fill: none;
  stroke-linecap: round;
  ${(props) => props.colorScheme === 'inverted'
    ? css `
          stroke: ${props.theme.cmp.color.feedback.progressIndicator.inverted
        .surface.trainLine.default};
        `
    : css `
          stroke: ${props.theme.cmp.color.feedback.progressIndicator.monochrome
        .surface.trainLine.default};
        `}
  stroke-width: ${(props) => props.size === 'lg' || (typeof props.size === 'number' && props.size >= 48)
    ? CMP_BORDER_WIDTH_FEEDBACK_PROGRESS_INDICATOR_SPINNER_LG
    : CMP_BORDER_WIDTH_FEEDBACK_PROGRESS_INDICATOR_SPINNER_MD};

  ${(props) => {
    const circumference = calculateCircumference(props.dimensions, 3);
    const offset = circumference - ((props.progress || 0) / 100) * circumference;
    if (props.type === 'infinite') {
        return css `
        animation: ${continuousCircularLoadingKeyframes(circumference)} 1.5s
          cubic-bezier(0.3, 0.95, 0.65, 0.9) infinite both;
      `;
    }
    return css `
      stroke-dasharray: ${circumference};
      stroke-dashoffset: ${offset};
    `;
}}
`;
export const StyledProgressIndicatorContainer = styled.div `
  margin: ${(props) => getMargins(props.margin)};
  display: ${(props) => (props.variant === 'spinner' ? 'inline-flex' : 'flex')};
  flex-direction: column;
  gap: ${SYS_SPACE_FIXED_100};
  ${(props) => props.variant === 'spinner' &&
    css `
      align-items: center;
    `}
`;
export const StyledLabel = styled.div `
  color: ${(props) => props.colorScheme === 'idle'
    ? props.theme.cmp.color.feedback.progressIndicator.foreground.text.default
    : props.theme.cmp.color.feedback.progressIndicator.foreground.text
        .inverted};

  ${(props) => props.size === 'sm'
    ? getTypographyStyles(CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_SM_LABEL)
    : props.size === 'md'
        ? getTypographyStyles(CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_MD_LABEL)
        : getTypographyStyles(CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_LG_LABEL)};
`;
export const StyledProgressBar = styled.div `
  margin: ${(props) => getMargins(props.margin)};
  position: relative;
  width: 100%;
  height: ${SYS_SIZE_FIXED_100};
  overflow: hidden;
  background-color: ${(props) => props.colorScheme === 'inverted'
    ? props.theme.cmp.color.feedback.progressIndicator.inverted.surface
        .trackLine.default
    : props.theme.cmp.color.feedback.progressIndicator.monochrome.surface
        .trackLine.default};
  border-radius: ${SYS_BORDER_RADIUS_MAX};
`;
export const StyledProgress = styled.div `
  display: inline-flex;
  color: ${(props) => props.theme.cmp.color.feedback.progressIndicator.foreground.text.default};
  ${getTypographyStyles(CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_MD_VALUE)}
  margin-left: ${(props) => props.progress}%;

  ${(props) => props.progress === 0
    ? css `
          transform: translateX(0%);
        `
    : props.progress === 100
        ? css `
            transform: translateX(-100%);
          `
        : css `
            transform: translateX(-50%);
          `}
`;
const continuousHorizontalLoadingKeyframes = keyframes `
  0% {
    left: 0%;
    width: 0%;
  }
  50% {
    left: 20%;
    width: 50%;
    animation-timing-function: cubic-bezier(0,0,0,1);
  }
  100% {
    left: 100%;
    width: 0%;
  }
`;
export const StyledProgressBarTrain = styled.div `
  position: absolute;
  top: 0%;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.colorScheme === 'inverted'
    ? props.theme.cmp.color.feedback.progressIndicator.inverted.surface
        .trainLine.default
    : props.theme.cmp.color.feedback.progressIndicator.monochrome.surface
        .trainLine.default};
  transform-origin: 0% 0%;
  border-radius: ${SYS_BORDER_RADIUS_MAX};

  ${(props) => props.type === 'infinite'
    ? css `
          animation: ${continuousHorizontalLoadingKeyframes} 1s
            cubic-bezier(0.85, 0, 1, 1) infinite;
        `
    : css `
          width: ${`${props.progress}%`};
          // TODO: missing token
          transition: transform ease-in-out 0.1s;
        `}
`;
export const StyledRangeContainer = styled.div `
  display: flex;
  justify-content: ${(props) => !props.rangeNumbers?.startString ? 'flex-end' : 'space-between'};
`;
export const StyledRangeNumber = styled.div `
  color: ${(props) => props.theme.cmp.color.feedback.progressIndicator.foreground.text.range
    .default};
  ${getTypographyStyles(CMP_TYPOGRAPHY_FEEDBACK_PROGRESS_INDICATOR_MD_RANGE)}
`;
export const StyledFiniteBarContainer = styled.div `
  margin: ${(props) => getMargins(props.margin)};
  display: flex;
  flex-direction: column;
  gap: ${SYS_SIZE_FIXED_100};
`;
//# sourceMappingURL=index.styles.js.map